// Copyright 2026, University of Maryland, All Rights Reserved
import { useQuery } from '@tanstack/react-query'

import { useServices } from '@/app-shell/services'
import { fetchKbSchema } from '../core/knowledgeBase.api'
import type { KbSchema } from '../types'

const QUERY_KEY = (id: string | null | undefined) => ['kb-schema', id ?? null] as const

/**
 * Fetch the KB-item schema for an instance, if it has one. Returns `null` when
 * the instance has no schema (free-form items) or the schema lookup is empty.
 */
export function useKbItemSchema(schemaId: string | null | undefined): {
  schema: KbSchema | null
  isLoading: boolean
} {
  const services = useServices()
  const client = services.getClient()

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY(schemaId),
    queryFn: () => fetchKbSchema(client, schemaId as string),
    enabled: !!schemaId,
    staleTime: 5 * 60_000,
  })

  return { schema: data ?? null, isLoading: !!schemaId && isLoading }
}
